import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { WatcherTypeFormController } from 'components/forms/WatcherTypeForm';
import { useAxios } from 'hooks/useAxios';
import { useSnackbar } from 'notistack';
import { WATCHER_TYPES_CACHE_KEY } from 'services/queryKeys';
import { createFieldValidationErrorObject } from 'services/rest/utils';
import { keysToCamelCase, keysToSnakeCase } from 'utils/formatters';

export const Component = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const reactQueryClient = useQueryClient();
  const navigate = useNavigate();
  const { post } = useAxios();

  const {
    mutateAsync: createWatcherType,
    isLoading: isCreating,
    error,
  } = useMutation(
    (formData: Record<string, unknown>) =>
      post(
        `/watcher-types/`,
        keysToSnakeCase({
          externalIdentifier: formData.externalIdentifier,
          deviceTypeId: formData.deviceType?.deviceTypeId,
        }) as Record<string, unknown>
      ),
    {
      onSuccess: ({ data }) => {
        enqueueSnackbar(
          t('main:genericSuccesses.create', {
            entity: t('entities:watcherType.nameSingular').toLowerCase(),
            identifier: `${data.external_identifier}`,
          }),
          { variant: 'success' }
        );
        reactQueryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === WATCHER_TYPES_CACHE_KEY()[0],
        });
        navigate('/watcher-types');
      },
      onError: (_err: AxiosError, watcherType) => {
        enqueueSnackbar(
          t('main:genericErrors.create', {
            entity: t('entities:watcherType.nameSingular').toLowerCase(),
            identifier: `${watcherType?.externalIdentifier}`,
          }),
          { variant: 'error' }
        );
      },
    }
  );

  const errorFieldObject = useMemo(
    () => keysToCamelCase(createFieldValidationErrorObject(error as AxiosError, t)),
    [error, t]
  );

  return (
    <>
      <Typography variant="h3" mb={2}>
        {t('entities:watcherType.new')}
      </Typography>
      <WatcherTypeFormController onSubmit={createWatcherType} isLoading={isCreating} fieldErrors={errorFieldObject} />
    </>
  );
};

Component.displayName = 'AddWatcherType';
